
import Component from "vue-class-component"
import PageHeader from "@/superannuation/PageHeader.vue"
import HelpIcon from "@/ui/HelpIcon.vue"
import Vue from "vue"
import {date, QBadge, QBtn, QSelect} from "quasar"
import Axios, {AxiosError, AxiosRequestConfig, AxiosResponse} from "axios"
import {Batch, Employer, Member, Platform} from "@/staff/Statement"
import _ from "lodash"
import {isToday} from "date-fns"
import SelectDropDown from "@/components/SelectDropDown.vue"
import PopupDate from "@/components/PopupDate.vue"
import Message from "@/components/Message.vue"
import ExpandButton from "@/components/ExpandButton.vue"
import CloseButton from "@/components/CloseButton.vue"
import AddDeleteButton from "@/components/AddDeleteButton.vue"
import {Inject} from "vue-property-decorator"
import {AuthenticationService} from "@/AuthenticationService"

@Component({
  components: {
    QBtn,
    CloseButton,
    AddDeleteButton,
    ExpandButton,
    PopupDate,
    Message,
    PageHeader,
    HelpIcon,
    QSelect,
    QBadge,
    SelectDropDown,
  },
})
export default class InsuranceStatementView extends Vue {
  @Inject()
  authenticationService: AuthenticationService

  readonly COMPLETED = "COMPLETED"
  readonly FAILED = "FAILED"
  readonly valid = [this.COMPLETED, this.FAILED]
  readonly running = ["STARTING", "STARTED", "STOPPING"]

  readonly batchColumns = [
    {name: "id", label: "Batch", field: "id"},
    {name: "platform", label: "Platform", field: "platform", sortable: true},
    {name: "company", label: "Employer", field: "company", sortable: true},
    {
      name: "at",
      label: "Generated",
      field: "at",
      sortable: true,
      format: (ds: string) => date.formatDate(ds, "Do MMM YY"),
    },
    {name: "status", label: "Status", field: "status", sortable: true},
    {name: "successful", label: "Run", field: "successful", sortable: true},
    {name: "failed", label: "Failed", field: "failed", sortable: true},
    {
      name: "sent",
      label: "Sent",
      field: "sent",
      sortable: true,
      format: (ds: string) => date.formatDate(ds, "Do MMM YY"),
    },
    {name: "delivery", label: "Delivery", field: "delivery", sortable: true},
    {name: "delivered", label: "Delivered", field: "delivered", sortable: true},
  ]

  readonly memberColumns = [
    {name: "number", label: "Member #", field: "number", sortable: true},
    {name: "username", label: "Login", field: "username", sortable: true},
    {name: "name", label: "Name", field: "name", sortable: true},
    {
      name: "dob",
      label: "DOB",
      field: "dob",
      sortable: true,
      format: (ds: string) => date.formatDate(ds, "DD/MM/YYYY"),
    },
  ]

  $refs: {
    memberSelect: SelectDropDown
  }

  platformId: string | null = null
  companyId: string | null = null
  memberId: string | null = null

  companies: Array<Employer> | null = null

  batches: Array<Batch> | null = null
  members: Array<Member> | null = null

  maintainBatch: Batch | null = null
  sendMember: Member | null = null
  batchId: string | null = null
  batchCompanyId: string | null = null
  batchEncrypted = false

  readError = false
  writeError = false
  readMessage: string | null = null
  writeMessage: string | null = null
  rejection: string | null = null

  dataInProgress = false
  showBatch = false
  showSend = false
  batchUpdate = false
  isAdmin = false

  timer: NodeJS.Timeout | null = null

  async created() {
    this.isAdmin = await this.authenticationService.isAdmin()
    await this.load()
  }

  private async load() {
    await this.getCompanies()
    await this.getBatches()
  }

  get error(): boolean {
    return this.readError || this.writeError
  }

  get message(): string | null {
    return this.writeMessage == null ? this.readMessage : this.writeMessage
  }

  private isCompleted(batch: Batch | null): boolean {
    return batch !== null && batch.status === this.COMPLETED
  }

  private isAnyGenerated(batch: Batch | null): boolean {
    return this.isCompleted(batch) && batch!.successful > 0
  }

  private isSending(batch: Batch): boolean {
    return batch.delivery !== null && this.running.includes(batch.delivery)
  }

  private isDeliveryFailed(batch: Batch): boolean {
    return batch.delivery === this.FAILED && batch.delivered === 0
  }

  private isOperableBatch(batch: Batch | null): boolean {
    return (
      batch !== null &&
      this.valid.includes(batch.status) &&
      (batch.sent === null || this.isDeliveryFailed(batch))
    )
  }

  get isBatchesRunning(): boolean {
    return _.some(
      this.batches,
      b =>
        (this.running.includes(b.status) && isToday(new Date(b.at))) ||
        (_.every([b.sent, b.delivery], _.negate(_.isNull)) &&
          this.running.includes(b.delivery!) &&
          isToday(new Date(b.sent!)))
    )
  }

  get isGenerateDisabled(): boolean {
    return this.batchCompanyId === null
  }

  get isRegenerateDisabled(): boolean {
    return !this.isOperableBatch(this.maintainBatch)
  }

  private isDeletable(batch: Batch): boolean {
    return this.isOperableBatch(batch) || (this.isAdmin && batch.delivered === 0)
  }

  get isSendDisabled(): boolean {
    return (
      !this.isAnyGenerated(this.maintainBatch) ||
      this.isSending(this.maintainBatch!) ||
      !this.maintainBatch!.encrypted ||
      _.isEmpty(this.members) ||
      this.maintainBatch!.successful === this.maintainBatch!.delivered
    )
  }

  get isMembers(): boolean {
    return this.batchUpdate && this.members !== null
  }

  get isEncryptDisabled(): boolean {
    return this.isGenerateDisabled || (this.batchUpdate && this.isRegenerateDisabled)
  }

  get noMembersMessage(): string {
    return this.maintainBatch!.successful > 0
      ? "Members may have been re-attached to a newer batch"
      : "No valid Member statements were generated"
  }

  private async getCompanies() {
    await Axios.get(`/api/groupInsurance/employers?name=`).then((response: AxiosResponse) => {
      this.companies = response.data
    })
  }

  private async getBatches() {
    this.dataInProgress = true
    await Axios.get(`/api/groupInsurance/reportRuns`, this.filters())
      .then((response: AxiosResponse) => {
        this.readMessage = null
        this.batches = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.readError = true
        this.readMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private setIntervalAttempts(
    callback: () => Promise<void>,
    timeout: number,
    attempts: number
  ): NodeJS.Timeout {
    let x = 0
    void callback()
    const timer = setInterval(() => {
      if (++x === attempts || !this.isBatchesRunning) {
        window.clearInterval(timer)
      } else {
        void callback()
      }
    }, timeout)
    return timer
  }

  private async getMembers() {
    this.dataInProgress = true
    await Axios.get(`/api/groupInsurance/reportRuns/${this.batchId!}/members`)
      .then((response: AxiosResponse) => {
        this.readMessage = null
        this.members = response.data
      })
      .catch((reason: AxiosError<any>) => {
        this.readError = true
        this.readMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private filters(): AxiosRequestConfig | undefined {
    return this.unfiltered
      ? undefined
      : {
          params: {
            platformId: this.platformId,
            companyId: this.companyId,
          },
        }
  }

  get unfiltered(): boolean {
    return [this.platformId, this.companyId].every(_.isNull)
  }

  private defaultMaintainBatch(batch: Batch) {
    this.maintainBatch = batch
    this.batchId = batch.id
    this.batchCompanyId = batch.companyId
    this.batchEncrypted = batch.encrypted
    void this.getMembers()
  }

  private showUpdateBatch(batch: Batch) {
    this.defaultMaintainBatch(batch)
    this.batchUpdate = true
    this.showBatch = true
  }

  private showCreateBatch() {
    this.batchCompanyId = this.companyId
    this.batchEncrypted = false
    this.batchUpdate = false
    this.showBatch = true
  }

  private showSendPopup(member: Member) {
    this.sendMember = member
    this.showSend = true
  }

  private batchThen = () => {
    this.writeError = false
    this.writeMessage = null
    this.timer = this.setIntervalAttempts(this.getBatches, 5000, 6)
  }

  private async runBatch(url: string) {
    this.dataInProgress = true
    void (await Axios.post(url, null, {params: {encrypt: this.batchEncrypted}})
      .then(this.batchThen)
      .catch((reason: AxiosError<any>) => {
        this.writeError = true
        this.writeMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false)))
  }

  private async generateBatch() {
    this.showBatch = false
    if (!this.isGenerateDisabled) {
      const url = `/api/groupInsurance/generateStatements/${this.batchCompanyId!}`
      await this.runBatch(this.memberId === null ? url : `${url}?memberId=${this.memberId}`)
    }
  }

  private async regenerateBatch() {
    this.showBatch = false
    if (!this.isRegenerateDisabled) {
      await this.runBatch(`/api/groupInsurance/regenerateStatements/${this.batchId!}`)
    }
  }

  private async sendBatch() {
    this.dataInProgress = true
    if (!this.isSendDisabled) {
      void (await Axios.post(`/api/groupInsurance/sendStatementEmails/${this.batchId!}`)
        .then(this.batchThen)
        .catch((reason: AxiosError<any>) => {
          this.writeError = true
          this.writeMessage = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false)))
    }
  }

  private platformSelected(platform: Platform | null) {
    this.platformId = platform == null ? null : platform.id
    if (platform != null) {
      this.companyId = null
    }
    void this.getBatches()
  }

  private companySelected(company: Employer | null) {
    this.companyId = company == null ? null : company.id
    void this.getBatches()
  }

  private batchCompanySelected() {
    this.memberId = null
    this.$refs.memberSelect.model = null
  }

  private memberSelected(member: Member | null) {
    this.memberId = member == null ? null : member.id
  }

  private deleteThen = () => {
    void this.getBatches()
  }

  private async deleteBatch(batch: Batch) {
    await Axios.delete(`/api/groupInsurance/reportRuns/${batch.id}`)
      .then(this.deleteThen)
      .catch((reason: AxiosError<any>) => {
        this.writeError = true
        this.writeMessage = reason.response?.data.message
      })
      .finally(() => (this.dataInProgress = false))
  }

  private isValidEmail(email: string): boolean {
    return /[\da-z]+@[a-z]+\.[a-z]{2,3}/.test(email)
  }

  private async sendEmail() {
    if (this.sendMember !== null) {
      this.dataInProgress = true
      await Axios.post(`/api/groupInsurance/statement/${this.sendMember.id}`, null, {
        params: {address: this.sendMember.email},
      })
        .catch((reason: AxiosError<any>) => {
          this.writeError = true
          this.writeMessage = reason.response?.data.message
        })
        .finally(() => (this.dataInProgress = false))
    }
  }

  private openStatement(memberId: string) {
    window.open(`/api/groupInsurance/statement/${memberId}`, "_blank")
  }

  private packageDownloadUrl(batch: Batch): string {
    return `/api/groupInsurance/reportRuns/${batch.id}/package`
  }
}
